import "./MobileCertificateEditor.css";

import {
    FaArrowLeft,
    FaDatabase,
    FaEdit,
    FaEllipsisH,
    FaEye,
    FaFileAlt,
    FaFileCode,
    FaFilePdf,
    FaLayerGroup,
    FaMagnet,
    FaPlus,
    FaRedo,
    FaSearch,
    FaSave,
    FaTh,
    FaTimes,
    FaUndo
} from "react-icons/fa";
import EditorCanvas from "../EditorCanvas/EditorCanvas";
import EditorPageProperties from "../EditorPropiedades/EditorPageProperties";
import EditorPropiedades from "../EditorPropiedades/EditorPropiedades";
import EditorSidebar from "../EditorSidebar/EditorSidebar";
import CanvasZoomControls from "../CanvasZoomControls";

function MobileCertificateEditor({
    onBack,
    xsdName,
    xsdVersion,
    canUndo,
    canRedo,
    onUndo,
    onRedo,
    onSave,
    isSaving,
    activePanel,
    onSetActivePanel,
    elements,
    selectedElement,
    selected,
    onAddElement,
    onSelectElement,
    onMoveLayer,
    onToggleLayerVisibility,
    onUpdateElement,
    dynamicFields,
    onDeleteElement,
    onDuplicateElement,
    onBeginHistoryStep,
    pageStyle,
    onUpdatePageStyle,
    gridVisible,
    onToggleGrid,
    snapToGrid,
    onToggleSnap,
    zoom,
    onZoomChange,
    onFitZoom,
    onToggleCollapse,
    onPreview,
    onOpenSampleData,
    onDownloadJson,
    onGeneratePdf,
    pdfError
}) {
    const closePanel = () => onSetActivePanel(null);
    const togglePanel = panel => {
        if (panel === "properties" && !selected) return;
        onSetActivePanel(current => current === panel ? null : panel);
    };
    const selectElement = id => onSelectElement(id);
    const addElement = (type, position) => {
        onAddElement(type, position);
        closePanel();
    };
    const deleteElement = id => {
        onDeleteElement(id);
        closePanel();
    };
    const runAction = action => {
        closePanel();
        action();
    };

    return (
        <>
            <header className="mobile-editor-header">
                <button type="button" className="mobile-editor-back" aria-label="Regresar" onClick={onBack}>
                    <FaArrowLeft aria-hidden="true" />
                </button>
                <div className="mobile-editor-title">
                    <strong>Certificado personalizado</strong>
                    <span>
                        {xsdName
                            ? `XSD: ${xsdName}${xsdVersion ? ` · Versión ${xsdVersion}` : ""}`
                            : "Constructor visual"}
                    </span>
                </div>
                <div className="mobile-editor-history">
                    <button type="button" aria-label="Deshacer" title="Deshacer" onClick={onUndo} disabled={!canUndo}>
                        <FaUndo aria-hidden="true" />
                    </button>
                    <button type="button" aria-label="Rehacer" title="Rehacer" onClick={onRedo} disabled={!canRedo}>
                        <FaRedo aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        aria-label="Zoom del lienzo"
                        title="Ajustar el zoom del lienzo"
                        aria-expanded={activePanel === "zoom"}
                        aria-pressed={activePanel === "zoom"}
                        onClick={() => togglePanel("zoom")}
                    >
                        <FaSearch aria-hidden="true" />
                    </button>
                    <button type="button" aria-label="Guardar cambios" title="Guardar cambios localmente en este navegador" onClick={onSave} disabled={isSaving}>
                        <FaSave aria-hidden="true" />
                    </button>
                </div>
            </header>

            {pdfError && <div className="editor-print-error" role="alert">{pdfError}</div>}

            <main className="mobile-editor-workspace">
                <EditorCanvas
                    elements={elements}
                    selectedElement={selectedElement}
                    onSelectElement={selectElement}
                    onAddElement={addElement}
                    onUpdateElement={onUpdateElement}
                    onDeleteElement={deleteElement}
                    onDuplicateElement={onDuplicateElement}
                    onBeginHistoryStep={onBeginHistoryStep}
                    pageStyle={pageStyle}
                    gridVisible={gridVisible}
                    snapToGrid={snapToGrid}
                    zoom={zoom}
                />

                {activePanel && (
                    <button
                        type="button"
                        className="mobile-editor-backdrop"
                        aria-label="Cerrar panel activo"
                        onClick={closePanel}
                    />
                )}

                {activePanel === "components" && (
                    <div className="mobile-editor-sheet">
                        <EditorSidebar
                            onAddElement={addElement}
                            elements={elements}
                            selectedElement={selectedElement}
                            onSelectElement={selectElement}
                            onMoveLayer={onMoveLayer}
                            onToggleLayerVisibility={onToggleLayerVisibility}
                            componentsOpen
                            onCloseComponents={closePanel}
                        />
                    </div>
                )}

                {activePanel === "properties" && (
                    <div className="mobile-editor-sheet">
                        <EditorPropiedades
                            element={selected}
                            onUpdate={onUpdateElement}
                            dynamicFields={dynamicFields}
                            collapsed={false}
                            onToggleCollapse={onToggleCollapse}
                            panelOpen
                            onClosePanel={closePanel}
                        />
                    </div>
                )}

                {activePanel === "layers" && (
                    <div className="mobile-editor-sheet">
                        <EditorSidebar
                            showComponents={false}
                            onAddElement={addElement}
                            elements={elements}
                            selectedElement={selectedElement}
                            onSelectElement={selectElement}
                            onMoveLayer={onMoveLayer}
                            onToggleLayerVisibility={onToggleLayerVisibility}
                            layersOpen
                            layersModal
                            onCloseLayers={closePanel}
                        />
                    </div>
                )}

                {activePanel === "page" && (
                    <div className="mobile-editor-sheet">
                        <EditorPageProperties
                            pageStyle={pageStyle}
                            onChange={onUpdatePageStyle}
                            onClose={closePanel}
                            modal
                        />
                    </div>
                )}

                {activePanel === "zoom" && (
                    <section className="mobile-editor-sheet mobile-editor-zoom-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-editor-zoom-title">
                        <header className="mobile-editor-sheet-header">
                            <h2 id="mobile-editor-zoom-title">Zoom del lienzo</h2>
                            <button type="button" aria-label="Cerrar zoom" onClick={closePanel}>
                                <FaTimes aria-hidden="true" />
                            </button>
                        </header>
                        <CanvasZoomControls
                            zoom={zoom}
                            onZoomChange={onZoomChange}
                            onFit={onFitZoom}
                        />
                    </section>
                )}

                {activePanel === "actions" && (
                    <section className="mobile-editor-sheet mobile-editor-actions-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-editor-actions-title">
                        <header className="mobile-editor-sheet-header">
                            <h2 id="mobile-editor-actions-title">Más herramientas</h2>
                            <button type="button" aria-label="Cerrar herramientas" onClick={closePanel}>
                                <FaTimes aria-hidden="true" />
                            </button>
                        </header>
                        <div className="mobile-editor-actions-list">
                            <button type="button" aria-pressed={gridVisible} onClick={onToggleGrid}>
                                <FaTh aria-hidden="true" />
                                <span>Mostrar cuadrícula</span>
                            </button>
                            <button type="button" aria-pressed={snapToGrid} onClick={onToggleSnap}>
                                <FaMagnet aria-hidden="true" />
                                <span>Ajustar a cuadrícula</span>
                            </button>
                            <button type="button" onClick={() => runAction(onPreview)}>
                                <FaEye aria-hidden="true" />
                                <span>Previsualizar</span>
                            </button>
                            <button type="button" onClick={() => runAction(onOpenSampleData)}>
                                <FaDatabase aria-hidden="true" />
                                <span>Datos de ejemplo</span>
                            </button>
                            <button type="button" onClick={() => runAction(onDownloadJson)}>
                                <FaFileCode aria-hidden="true" />
                                <span>Descargar JSON</span>
                            </button>
                            <button type="button" onClick={() => runAction(onGeneratePdf)}>
                                <FaFilePdf aria-hidden="true" />
                                <span>Generar PDF</span>
                            </button>
                        </div>
                    </section>
                )}
            </main>

            <nav className="mobile-editor-tools" aria-label="Herramientas del editor">
                <button
                    type="button"
                    aria-label="Agregar componentes"
                    aria-expanded={activePanel === "components"}
                    aria-pressed={activePanel === "components"}
                    onClick={() => togglePanel("components")}
                >
                    <FaPlus aria-hidden="true" />
                    <span>Agregar</span>
                </button>
                <button
                    type="button"
                    aria-label="Propiedades del elemento"
                    aria-expanded={activePanel === "properties"}
                    aria-pressed={activePanel === "properties"}
                    disabled={!selected}
                    onClick={() => togglePanel("properties")}
                >
                    <FaEdit aria-hidden="true" />
                    <span>Propiedades</span>
                </button>
                <button
                    type="button"
                    aria-label="Capas"
                    aria-expanded={activePanel === "layers"}
                    aria-pressed={activePanel === "layers"}
                    onClick={() => togglePanel("layers")}
                >
                    <FaLayerGroup aria-hidden="true" />
                    <span>Capas</span>
                </button>
                <button
                    type="button"
                    aria-label="Propiedades de hoja"
                    aria-expanded={activePanel === "page"}
                    aria-pressed={activePanel === "page"}
                    onClick={() => togglePanel("page")}
                >
                    <FaFileAlt aria-hidden="true" />
                    <span>Hoja</span>
                </button>
                <button
                    type="button"
                    aria-label="Más herramientas"
                    aria-expanded={activePanel === "actions"}
                    aria-pressed={activePanel === "actions"}
                    onClick={() => togglePanel("actions")}
                >
                    <FaEllipsisH aria-hidden="true" />
                    <span>Más</span>
                </button>
            </nav>
        </>
    );
}

export default MobileCertificateEditor;
